"use client";

import ReactDOM from "react-dom";
import { type FC, type ReactNode, useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";

interface ModalProps {
  showDrawer: boolean;
  closeDrawer: () => void;
  children: ReactNode;
}

interface DrawerPortalProps {
  children: ReactNode;
  closeDrawer: () => void;
}

export const DrawerPortal = ({ children, closeDrawer }: DrawerPortalProps) => {
  const modalRoot = document.getElementById("drawer-root");

  useEffect(() => {
    document.body.style.overflow = "hidden";

    const closeOnEscapeKey = (e: KeyboardEvent) =>
      e.key === "Escape" ? closeDrawer() : null;

    document.body.addEventListener("keydown", closeOnEscapeKey);

    return () => {
      document.body.style.overflow = "auto";
      document.body.removeEventListener("keydown", closeOnEscapeKey);
    };
  }, [closeDrawer]);

  return ReactDOM.createPortal(
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="bg-themeBlue/5 backdrop-blur-sm w-[calc(100svw-2rem)] h-[calc(100svh-2rem)] fixed top-1/2 -translate-y-1/2 left-1/2 -translate-x-1/2 transition-all z-20 border rounded-[30px]"
    >
      <div className="bg-black/80 fill-grain overflow-hidden rounded-3xl absolute inset-2 z-50 border">
        <div className="p-4 h-full">{children}</div>
      </div>
    </motion.div>,
    modalRoot!
  );
};

export const Drawer: FC<ModalProps> = ({
  showDrawer,
  closeDrawer,
  children,
}) => (
  <AnimatePresence>
    {showDrawer ? (
      <DrawerPortal closeDrawer={closeDrawer}>{children}</DrawerPortal>
    ) : null}
  </AnimatePresence>
);
